/* ============================================================
   Amofii Quick-Add Popup — JS
   Instant embedded data + AJAX fallback with dynamic options,
   multi-variant selection, Shiprocket One-Click Checkout, and
   Shopify Cart Drawer integration.
   ============================================================ */

(function () {
  'use strict';

  const POPUP_ID    = 'amofii-quickadd-popup';
  const BACKDROP_ID = 'amofii-quickadd-backdrop';
  const CLOSE_ID    = 'amofii-quickadd-close';
  const CONTENT_ID  = 'amofii-qa-content';
  const LOADING_ID  = 'amofii-qa-loading';

  let popup, backdrop, content, loading;
  let currentProduct = null;
  let selectedVariant = null;
  let selectedOptions = [];

  /* ---- Init on DOMContentLoaded ---- */
  document.addEventListener('DOMContentLoaded', () => {
    popup    = document.getElementById(POPUP_ID);
    backdrop = document.getElementById(BACKDROP_ID);
    content  = document.getElementById(CONTENT_ID);
    loading  = document.getElementById(LOADING_ID);

    if (!popup) return;

    /* Close on backdrop click */
    backdrop.addEventListener('click', closePopup);
    const closeBtn = document.getElementById(CLOSE_ID);
    if (closeBtn) closeBtn.addEventListener('click', closePopup);

    /* Close on Escape */
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.getAttribute('aria-hidden') === 'false') {
        closePopup();
      }
    });

    /* Delegate trigger clicks — works for cards on all pages/grids */
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.amofii-quickadd-trigger');
      if (!trigger) return;
      
      // If the product is explicitly sold out or disabled, do not open
      if (trigger.hasAttribute('disabled') || trigger.classList.contains('disabled')) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      const handle = trigger.dataset.productHandle;
      if (handle) openQuickAdd(handle, trigger);
    });
  });

  /* ---- Open popup ---- */
  function openPopup() {
    if (!popup || !backdrop) return;
    popup.setAttribute('aria-hidden', 'false');
    backdrop.classList.add('amofii-qa-active');
    document.body.style.overflow = 'hidden';
  }

  /* ---- Close popup ---- */
  function closePopup() {
    if (!popup || !backdrop) return;
    popup.setAttribute('aria-hidden', 'true');
    popup.classList.remove('has-open-chart');
    backdrop.classList.remove('amofii-qa-active');
    document.body.style.overflow = '';
    
    /* Reset after animation */
    setTimeout(() => {
      if (content) {
        content.style.display = 'none';
        content.innerHTML = '';
      }
      if (loading) loading.style.display = 'block';
      currentProduct = null;
      selectedVariant = null;
      selectedOptions = [];
    }, 320);
  }

  /* ---- Fetch / Read product + open ---- */
  async function openQuickAdd(handle, triggerEl) {
    // 1. Check for instant embedded product data on card
    let directData = null;
    if (triggerEl) {
      const card = triggerEl.closest('.product-item, .product__item, .nisho-card, .product-card, .grid__item, .product-item__price-row') || triggerEl.parentElement;
      if (card) {
        const scriptData = card.querySelector(`script.amofii-product-data[data-handle="${handle}"]`) || card.querySelector('script.amofii-product-data');
        if (scriptData && scriptData.textContent) {
          try {
            directData = JSON.parse(scriptData.textContent);
          } catch (_) {}
        }
      }
    }

    if (directData && (directData.variants || directData.title)) {
      currentProduct = normalizeProductData(directData);
      openPopup();
      renderContent(currentProduct);
      return;
    }

    // 2. Otherwise show loading skeleton and fetch via AJAX
    if (loading) loading.style.display = 'block';
    if (content) {
      content.style.display = 'none';
      content.innerHTML = '';
    }
    openPopup();

    try {
      currentProduct = await fetchProduct(handle);
      renderContent(currentProduct);
    } catch (err) {
      console.error('[Amofii QuickAdd] Failed to load product:', handle, err);
      if (content) {
        content.innerHTML = `
          <div style="text-align:center;padding:30px 10px;">
            <p style="color:#c0392b;margin-bottom:14px;font-size:14px;">Failed to load product details.</p>
            <a href="/products/${handle}" class="amofii-qa-atc" style="display:inline-flex;width:auto;padding:0 24px;text-decoration:none;">View Product</a>
          </div>
        `;
        if (loading) loading.style.display = 'none';
        content.style.display = 'block';
      }
    }
  }

  /* ---- Fetch product with multiple fallback URLs ---- */
  async function fetchProduct(handle) {
    const cleanHandle = String(handle).trim().toLowerCase().replace(/^\/products\//, '').replace(/\.js$/, '');
    const rootUrl = window.routes?.root_url || window.Shopify?.routes?.root || '/';
    const cleanRoot = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';

    const urlsToTry = [
      `${cleanRoot}products/${encodeURIComponent(cleanHandle)}.js`,
      `/products/${encodeURIComponent(cleanHandle)}.js`,
      `${window.location.origin}/products/${encodeURIComponent(cleanHandle)}.js`,
      `/products/${encodeURIComponent(cleanHandle)}.json`
    ];

    for (const url of urlsToTry) {
      try {
        const res = await fetch(url, {
          headers: {
            'Accept': 'application/json, text/javascript, */*',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        if (res.ok) {
          const data = await res.json();
          if (data && (data.variants || data.product?.variants || data.title)) {
            return normalizeProductData(data.product || data);
          }
        }
      } catch (_) {}
    }

    throw new Error(`Product ${handle} could not be loaded.`);
  }

  /* Normalize options and media structure */
  function normalizeProductData(product) {
    if (!product) return product;

    // Normalize options
    if (product.options) {
      product.normalizedOptions = product.options.map((opt, index) => {
        if (typeof opt === 'string') {
          const optKey = `option${index + 1}`;
          const values = [...new Set(product.variants.map(v => v[optKey]).filter(Boolean))];
          return { name: opt, position: index + 1, values: values };
        } else if (typeof opt === 'object') {
          return {
            name: opt.name || `Option ${index + 1}`,
            position: opt.position || (index + 1),
            values: opt.values || []
          };
        }
        return { name: `Option ${index + 1}`, position: index + 1, values: [] };
      });
    } else {
      product.normalizedOptions = [];
    }

    // Normalize media / images
    let images = [];
    if (product.media && product.media.length > 0) {
      images = product.media.filter(m => m.media_type === 'image' || !m.media_type).map(m => {
        return m.src || (m.preview_image && m.preview_image.src) || (typeof m === 'string' ? m : '');
      }).filter(Boolean);
    }
    if (images.length === 0 && product.images && product.images.length > 0) {
      images = product.images.map(img => typeof img === 'string' ? img : (img.src || '')).filter(Boolean);
    }
    if (images.length === 0 && product.featured_image) {
      images = [typeof product.featured_image === 'string' ? product.featured_image : (product.featured_image.src || '')];
    }
    product.normalizedImages = images;

    return product;
  }

  /* ---- Render popup content ---- */
  function renderContent(product) {
    // Initial variant selection: first available variant or first variant
    const defaultVariant = product.variants?.find(v => v.available) || product.variants?.[0] || null;
    selectedVariant = defaultVariant;

    // Initialize selected options array based on default variant
    selectedOptions = [];
    if (defaultVariant && product.normalizedOptions.length > 0) {
      product.normalizedOptions.forEach((opt, idx) => {
        const val = defaultVariant[`option${idx + 1}`] || opt.values[0] || '';
        selectedOptions[idx] = val;
      });
    }

    /* -- Image Gallery HTML -- */
    const images = product.normalizedImages || [];
    let imgsHtml = '';
    if (images.length > 0) {
      const displayImgs = images.slice(0, 6);
      imgsHtml = `
        <div class="amofii-qa-images-container">
          <div class="amofii-qa-images" id="amofii-qa-images-list">
            ${displayImgs.map((imgSrc, i) => `
              <div class="amofii-qa-img-wrap${i === 0 ? ' active' : ''}" data-img-index="${i}">
                <img
                  src="${getSizedImageUrl(imgSrc, 300)}"
                  alt="${escapeHtml(product.title)}"
                  loading="lazy"
                  width="120"
                  height="160"
                />
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    /* -- Tags HTML -- */
    const allowedTags = (product.tags || [])
      .filter(t => typeof t === 'string' && !t.startsWith('__') && t.length < 35)
      .slice(0, 4);
    const tagsHtml = allowedTags.length
      ? `<div class="amofii-qa-tags">${allowedTags.map(t => `<span class="amofii-qa-tag">${escapeHtml(t)}</span>`).join('')}</div>`
      : '';

    /* -- Price HTML -- */
    const initialPrice = selectedVariant ? selectedVariant.price : product.price;
    const initialComparePrice = selectedVariant ? selectedVariant.compare_at_price : product.compare_at_price;
    const priceFormatted = formatMoney(initialPrice);
    const comparePriceFormatted = initialComparePrice > initialPrice ? formatMoney(initialComparePrice) : '';

    /* -- Options HTML (Size, Color, Material, etc.) -- */
    let optionsHtml = '';
    let hasSizeOption = false;
    if (product.normalizedOptions && product.normalizedOptions.length > 0 && !hasOnlyDefaultVariant(product)) {
      optionsHtml = product.normalizedOptions.map((opt, optIndex) => {
        const optName = opt.name;
        const isColor = /^colou?r$/i.test(optName) || /^shade$/i.test(optName);
        const isSize = /^size$/i.test(optName);
        if (isSize) hasSizeOption = true;
        const selectedVal = selectedOptions[optIndex] || '';

        const sizeChartBtnHtml = isSize ? `
          <button type="button" class="amofii-qa-size-guide-btn" id="amofii-qa-size-guide-trigger" aria-label="Open Size Chart">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L2.7 8.7a2.4 2.4 0 0 1 0-3.4l2.6-2.6a2.4 2.4 0 0 1 3.4 0l12.6 12.6z"/>
              <path d="m14.5 12.5 2-2"/>
              <path d="m11.5 9.5 2-2"/>
              <path d="m8.5 6.5 2-2"/>
              <path d="m17.5 15.5 2-2"/>
            </svg>
            <span>Size Chart</span>
          </button>
        ` : '';

        return `
          <div class="amofii-qa-option-group" data-option-index="${optIndex}">
            <div class="amofii-qa-option-header">
              <span class="amofii-qa-option-label">${escapeHtml(optName)}: <strong class="amofii-qa-selected-val" id="amofii-qa-opt-val-${optIndex}">${escapeHtml(selectedVal)}</strong></span>
              ${sizeChartBtnHtml}
            </div>
            <div class="amofii-qa-option-values ${isColor ? 'amofii-qa-color-values' : 'amofii-qa-button-values'}">
              ${opt.values.map(val => {
                const isSelected = val === selectedVal;
                const isAvailable = checkOptionAvailability(product, optIndex, val);
                
                if (isColor) {
                  const colorVariant = product.variants.find(v => v[`option${optIndex + 1}`] === val && v.featured_image);
                  const colorImg = colorVariant?.featured_image?.src || colorVariant?.featured_image || '';
                  
                  return `
                    <button
                      type="button"
                      class="amofii-qa-color-btn${isSelected ? ' selected' : ''}${!isAvailable ? ' is-unavailable' : ''}"
                      data-option-index="${optIndex}"
                      data-value="${escapeHtml(val)}"
                      title="${escapeHtml(val)}"
                      aria-label="${escapeHtml(val)}"
                    >
                      ${colorImg ? `<img src="${getSizedImageUrl(colorImg, 80)}" alt="${escapeHtml(val)}" />` : `<span class="amofii-qa-color-fallback">${escapeHtml(val)}</span>`}
                    </button>
                  `;
                }

                return `
                  <button
                    type="button"
                    class="amofii-qa-opt-btn${isSelected ? ' selected' : ''}${!isAvailable ? ' is-unavailable' : ''}"
                    data-option-index="${optIndex}"
                    data-value="${escapeHtml(val)}"
                  >
                    ${escapeHtml(val)}
                  </button>
                `;
              }).join('')}
            </div>
          </div>
        `;
      }).join('');
    }

    /* -- Size Chart Modal HTML -- */
    const sizeChartModalHtml = `
      <div class="amofii-qa-size-chart-modal" id="amofii-qa-size-chart-modal" aria-hidden="true">
        <div class="amofii-qa-sc-header">
          <div class="amofii-qa-sc-title-wrap">
            <h3 class="amofii-qa-sc-title">Size Guide</h3>
            <p class="amofii-qa-sc-subtitle">These are <strong>to-fit body measurements</strong> (not garment measurement). If you have already purchased an item from us, we recommend you select the same size.</p>
          </div>
          <button type="button" class="amofii-qa-sc-close" id="amofii-qa-sc-close" aria-label="Close size chart">
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="amofii-qa-sc-body">
          <div class="amofii-qa-sc-table-wrap">
            <table class="amofii-qa-sc-table" id="amofii-qa-sc-table">
              <thead>
                <tr>
                  <th>SIZE</th>
                  <th>SHOULDER</th>
                  <th>BUST</th>
                  <th>WAIST</th>
                  <th>WAIST RANGE</th>
                  <th>HIP</th>
                  <th>US</th>
                  <th>UK</th>
                </tr>
              </thead>
              <tbody>
                <tr data-size="XS"><td><strong>XS</strong></td><td>14</td><td>32</td><td>28</td><td>26–28</td><td>35</td><td>2</td><td>6</td></tr>
                <tr data-size="S"><td><strong>S</strong></td><td>14.5</td><td>34</td><td>30</td><td>28–30</td><td>37</td><td>4</td><td>8</td></tr>
                <tr data-size="M"><td><strong>M</strong></td><td>15</td><td>36</td><td>32</td><td>30–32</td><td>39</td><td>6</td><td>10</td></tr>
                <tr data-size="L"><td><strong>L</strong></td><td>15.5</td><td>38</td><td>34</td><td>32–34</td><td>41</td><td>8</td><td>12</td></tr>
                <tr data-size="XL"><td><strong>XL</strong></td><td>16</td><td>40</td><td>36</td><td>34–36</td><td>43</td><td>10</td><td>14</td></tr>
                <tr data-size="2XL"><td><strong>2XL</strong></td><td>16.5</td><td>42</td><td>38</td><td>36–38</td><td>45</td><td>12</td><td>16</td></tr>
                <tr data-size="3XL"><td><strong>3XL</strong></td><td>17</td><td>44</td><td>40</td><td>38–40</td><td>47</td><td>14</td><td>18</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;

    /* -- Add to Cart & Buy It Now buttons -- */
    const isAvailable = selectedVariant ? selectedVariant.available : product.available;
    const atcButtonText = isAvailable ? 'ADD TO CART' : 'SOLD OUT';

    /* -- Full popup content construction -- */
    content.innerHTML = `
      ${imgsHtml}
      ${tagsHtml}
      <div class="amofii-qa-title-row">
        <h2 class="amofii-qa-title">${escapeHtml(product.title)}</h2>
        <div class="amofii-qa-price-wrap">
          <span class="amofii-qa-price" id="amofii-qa-price">${priceFormatted}</span>
          <span class="amofii-qa-price-compare" id="amofii-qa-compare-price" style="${comparePriceFormatted ? '' : 'display:none;'}">${comparePriceFormatted}</span>
        </div>
      </div>

      ${optionsHtml}

      <div class="amofii-qa-error" id="amofii-qa-error" role="alert"></div>

      <div class="amofii-qa-actions">
        <button
          type="button"
          class="amofii-qa-atc"
          id="amofii-qa-atc-btn"
          ${!isAvailable ? 'disabled' : ''}
        >
          <span class="amofii-qa-btn-text">${atcButtonText}</span>
        </button>

        <button
          type="button"
          class="amofii-qa-buynow shiprocket-headless"
          id="amofii-qa-buynow-btn"
          ${!isAvailable ? 'disabled' : ''}
        >
          <span class="amofii-qa-btn-text">BUY IT NOW</span>
        </button>
      </div>

      <div class="amofii-qa-footer">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="15" height="15">
          <path d="M4 12V7a8 8 0 0116 0v5"/>
          <rect x="2" y="12" width="20" height="10" rx="2"/>
        </svg>
        <span>Easy 7 Days Returns &amp; Exchange • COD Available</span>
      </div>

      ${sizeChartModalHtml}
    `;

    if (loading) loading.style.display = 'none';
    content.style.display = 'block';

    /* -- Bind events -- */
    bindPopupEvents(product);
  }

  /* ---- Check if product only has Default Title variant ---- */
  function hasOnlyDefaultVariant(product) {
    if (!product.variants || product.variants.length === 0) return true;
    return product.variants.length === 1 && (
      product.variants[0].title === 'Default Title' ||
      product.variants[0].option1 === 'Default Title' ||
      product.options?.[0] === 'Title'
    );
  }

  /* ---- Check option availability ---- */
  function checkOptionAvailability(product, optIndex, optVal) {
    const optKey = `option${optIndex + 1}`;
    return product.variants.some(v => v[optKey] === optVal && v.available);
  }

  /* ---- Bind Interactive Events ---- */
  function bindPopupEvents(product) {
    /* Image thumbnails click */
    content.querySelectorAll('.amofii-qa-img-wrap').forEach(wrap => {
      wrap.addEventListener('click', () => {
        content.querySelectorAll('.amofii-qa-img-wrap').forEach(w => w.classList.remove('active'));
        wrap.classList.add('active');
      });
    });

    /* Option buttons click (Size, generic, Color) */
    content.querySelectorAll('.amofii-qa-opt-btn, .amofii-qa-color-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const optIndex = parseInt(btn.dataset.optionIndex, 10);
        const optValue = btn.dataset.value;

        // Update selected button in this group
        const group = btn.closest('.amofii-qa-option-group');
        if (group) {
          group.querySelectorAll('.amofii-qa-opt-btn, .amofii-qa-color-btn').forEach(b => b.classList.remove('selected'));
          btn.classList.add('selected');
        }

        // Update label text
        const labelEl = document.getElementById(`amofii-qa-opt-val-${optIndex}`);
        if (labelEl) labelEl.textContent = optValue;

        // Update selected options state
        selectedOptions[optIndex] = optValue;

        // Match Shopify variant
        onVariantChange(product);
      });
    });

    /* Size Chart Modal Toggle */
    const scTrigger = content.querySelector('#amofii-qa-size-guide-trigger');
    const scModal = content.querySelector('#amofii-qa-size-chart-modal');
    const scClose = content.querySelector('#amofii-qa-sc-close');

    if (scTrigger && scModal) {
      scTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        scModal.classList.add('is-open');
        scModal.setAttribute('aria-hidden', 'false');
        if (popup) popup.classList.add('has-open-chart');

        // Highlight active size in table if selected
        const sizeOptionIndex = product.normalizedOptions ? product.normalizedOptions.findIndex(opt => /^size$/i.test(opt.name)) : -1;
        if (sizeOptionIndex !== -1 && selectedOptions[sizeOptionIndex]) {
          const curSize = selectedOptions[sizeOptionIndex];
          scModal.querySelectorAll('tbody tr').forEach(tr => {
            if (tr.dataset.size === curSize) {
              tr.classList.add('active-size');
            } else {
              tr.classList.remove('active-size');
            }
          });
        }
      });
    }

    if (scClose && scModal) {
      scClose.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        scModal.classList.remove('is-open');
        scModal.setAttribute('aria-hidden', 'true');
        if (popup) popup.classList.remove('has-open-chart');
      });
    }

    /* Unit toggle (in / cm) */
    if (scModal) {
      const unitBtns = scModal.querySelectorAll('.amofii-qa-unit-btn');
      const tableCells = scModal.querySelectorAll('#amofii-qa-sc-table td[data-in]');
      unitBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const unit = btn.dataset.unit;
          unitBtns.forEach(b => b.classList.remove('active'));
          btn.classList.add('active');

          tableCells.forEach(td => {
            td.textContent = td.getAttribute(`data-${unit}`) || td.dataset.in;
          });
        });
      });

      /* Clicking a row in the size chart selects that size */
      scModal.querySelectorAll('tbody tr').forEach(tr => {
        tr.addEventListener('click', () => {
          const sizeVal = tr.dataset.size;
          if (!sizeVal) return;

          const sizeOptionIndex = product.normalizedOptions ? product.normalizedOptions.findIndex(opt => /^size$/i.test(opt.name)) : -1;
          if (sizeOptionIndex !== -1) {
            const optBtn = content.querySelector(`.amofii-qa-opt-btn[data-option-index="${sizeOptionIndex}"][data-value="${sizeVal}"]`);
            if (optBtn) {
              optBtn.click();
            }
            scModal.classList.remove('is-open');
            scModal.setAttribute('aria-hidden', 'true');
            if (popup) popup.classList.remove('has-open-chart');
          }
        });
      });
    }

    /* Add to Cart */
    const atcBtn = document.getElementById('amofii-qa-atc-btn');
    if (atcBtn) {
      atcBtn.addEventListener('click', () => handleAddToCart(product));
    }

    /* Buy It Now (Shiprocket One-Click Checkout) */
    const buyNowBtn = document.getElementById('amofii-qa-buynow-btn');
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', () => handleBuyNow(product));
    }
  }

  /* ---- Variant change handler ---- */
  function onVariantChange(product) {
    const errorEl = document.getElementById('amofii-qa-error');
    if (errorEl) errorEl.textContent = '';

    // Find matching variant based on all selectedOptions
    const matchedVariant = product.variants.find(v => {
      for (let i = 0; i < selectedOptions.length; i++) {
        const key = `option${i + 1}`;
        if (v[key] != null && selectedOptions[i] != null) {
          if (v[key] !== selectedOptions[i]) return false;
        }
      }
      return true;
    });

    selectedVariant = matchedVariant || null;

    const priceEl = document.getElementById('amofii-qa-price');
    const comparePriceEl = document.getElementById('amofii-qa-compare-price');
    const atcBtn = document.getElementById('amofii-qa-atc-btn');
    const buyNowBtn = document.getElementById('amofii-qa-buynow-btn');

    if (matchedVariant) {
      // Update Price
      if (priceEl) priceEl.textContent = formatMoney(matchedVariant.price);
      if (comparePriceEl) {
        if (matchedVariant.compare_at_price > matchedVariant.price) {
          comparePriceEl.textContent = formatMoney(matchedVariant.compare_at_price);
          comparePriceEl.style.display = 'inline-block';
        } else {
          comparePriceEl.style.display = 'none';
        }
      }

      // Update buttons state
      if (matchedVariant.available) {
        if (atcBtn) {
          atcBtn.disabled = false;
          atcBtn.querySelector('.amofii-qa-btn-text').textContent = 'ADD TO CART';
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = false;
        }
      } else {
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.querySelector('.amofii-qa-btn-text').textContent = 'SOLD OUT';
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = true;
        }
      }

      // If variant has featured image, activate corresponding thumbnail
      if (matchedVariant.featured_image) {
        const variantImgSrc = matchedVariant.featured_image.src || matchedVariant.featured_image;
        if (variantImgSrc) {
          const imgs = content.querySelectorAll('.amofii-qa-img-wrap img');
          imgs.forEach(img => {
            const cleanImgSrc = img.src.split('?')[0].split('&')[0];
            const cleanVariantSrc = variantImgSrc.split('?')[0].split('&')[0];
            if (cleanImgSrc.includes(cleanVariantSrc) || cleanVariantSrc.includes(cleanImgSrc)) {
              content.querySelectorAll('.amofii-qa-img-wrap').forEach(w => w.classList.remove('active'));
              img.closest('.amofii-qa-img-wrap')?.classList.add('active');
            }
          });
        }
      }
    } else {
      // Variant combination not available
      if (atcBtn) {
        atcBtn.disabled = true;
        atcBtn.querySelector('.amofii-qa-btn-text').textContent = 'UNAVAILABLE';
      }
      if (buyNowBtn) {
        buyNowBtn.disabled = true;
      }
    }
  }

  /* ---- Add to Cart Handler ---- */
  async function handleAddToCart(product) {
    const errorEl = document.getElementById('amofii-qa-error');
    const atcBtn  = document.getElementById('amofii-qa-atc-btn');

    const variantId = selectedVariant ? selectedVariant.id : (product.variants?.[0]?.id || null);
    if (!variantId) {
      if (errorEl) errorEl.textContent = 'Please select your options.';
      return;
    }

    if (selectedVariant && !selectedVariant.available) {
      if (errorEl) errorEl.textContent = 'This variant is currently sold out.';
      return;
    }

    // Loading State
    if (atcBtn) {
      atcBtn.classList.add('amofii-qa-loading-state');
      const textSpan = atcBtn.querySelector('.amofii-qa-btn-text');
      if (textSpan) textSpan.textContent = 'Adding...';
    }
    if (errorEl) errorEl.textContent = '';

    try {
      const payload = {
        id: variantId,
        quantity: 1,
        sections: 'minicart-form',
        sections_url: window.location.pathname
      };

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.description || errData.message || 'Could not add to cart.');
      }

      const response = await res.json();

      // Close Quick Add popup
      closePopup();

      // Update minicart drawer HTML if rendered section is returned
      if (response.sections && response.sections['minicart-form']) {
        const minicartEl = document.getElementById('minicart-form');
        if (minicartEl) {
          const parsed = new DOMParser().parseFromString(
            response.sections['minicart-form'],
            'text/html'
          );
          const newContent = parsed.querySelector('#minicart-form');
          if (newContent) {
            minicartEl.innerHTML = newContent.innerHTML;
          }
        }
      }

      // Re-bind cart drawer events
      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && typeof cartNotification.cartAction === 'function') {
        cartNotification.cartAction();
      }

      // Update cart count badges, header total, and free shipping bar
      await updateCartState();

      // Open the cart drawer
      if (cartNotification && typeof cartNotification.open === 'function') {
        cartNotification.open();
      }

      // Dispatch global events for other theme listeners
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: response } }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));

    } catch (err) {
      console.error('[Amofii QuickAdd] Error adding to cart:', err);
      if (errorEl) {
        errorEl.textContent = err.message || 'Failed to add item to cart. Please try again.';
      }
      if (atcBtn) {
        atcBtn.classList.remove('amofii-qa-loading-state');
        const textSpan = atcBtn.querySelector('.amofii-qa-btn-text');
        if (textSpan) textSpan.textContent = 'ADD TO CART';
      }
    }
  }

  /* ---- Buy It Now with Shiprocket One-Click Checkout ---- */
  async function handleBuyNow(product) {
    const errorEl = document.getElementById('amofii-qa-error');
    const buyBtn  = document.getElementById('amofii-qa-buynow-btn');

    const variantId = selectedVariant ? selectedVariant.id : (product.variants?.[0]?.id || null);
    if (!variantId) {
      if (errorEl) errorEl.textContent = 'Please select your options.';
      return;
    }

    if (selectedVariant && !selectedVariant.available) {
      if (errorEl) errorEl.textContent = 'This variant is currently sold out.';
      return;
    }

    if (buyBtn) {
      buyBtn.classList.add('amofii-qa-loading-state');
      const textSpan = buyBtn.querySelector('.amofii-qa-btn-text');
      if (textSpan) textSpan.textContent = 'Processing...';
    }
    if (errorEl) errorEl.textContent = '';

    try {
      // 1. Add item to cart first so Shiprocket has the exact items/token
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: 'minicart-form',
          sections_url: window.location.pathname
        })
      });

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.description || errData.message || 'Could not process order.');
      }

      // Update cart state
      await updateCartState();

      // Close popup
      closePopup();

      // 2. Invoke Shiprocket One-Click Checkout
      if (window.HeadlessCheckout && typeof window.HeadlessCheckout.checkout === 'function') {
        window.HeadlessCheckout.checkout();
        return;
      }

      if (window.HeadlessCheckout && typeof window.HeadlessCheckout.buyCart === 'function') {
        window.HeadlessCheckout.buyCart();
        return;
      }

      if (typeof window.shiprocketCheckoutBuyCartHandler === 'function') {
        window.shiprocketCheckoutBuyCartHandler();
        return;
      }

      // Click existing cart checkout button (which Shiprocket intercepts on cart/drawer)
      const checkoutBtn = document.querySelector('.btn-checkout, .cart__checkout-button, button[name="checkout"]');
      if (checkoutBtn) {
        checkoutBtn.click();
        return;
      }

      // Fallback
      window.location.href = '/checkout';

    } catch (err) {
      console.error('[Amofii QuickAdd] Error in Buy Now:', err);
      if (errorEl) {
        errorEl.textContent = err.message || 'Failed to start checkout. Please try again.';
      }
      if (buyBtn) {
        buyBtn.classList.remove('amofii-qa-loading-state');
        const textSpan = buyBtn.querySelector('.amofii-qa-btn-text');
        if (textSpan) textSpan.textContent = 'BUY IT NOW';
      }
    }
  }

  /* ---- Synchronize Cart State across theme ---- */
  async function updateCartState() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      if (!cart || cart.item_count == null) return;

      const count = cart.item_count;

      // Update all badge elements
      document.querySelectorAll('.cart-count, [data-cart-count], .header__cart-badge, .cart-count-bubble').forEach(el => {
        if (el.classList.contains('cart-count-drawer')) {
          el.innerHTML = `(${count})`;
        } else {
          el.innerHTML = count > 99 ? '99+' : count;
        }
        if (count > 0) el.classList.remove('hidden');
      });

      document.querySelectorAll('h-count .cart-count').forEach(badge => {
        badge.textContent = count > 99 ? '99+' : count;
      });

      // Update header total price if custom element exists
      const headerTotal = document.querySelector('header-total-price');
      if (headerTotal && typeof headerTotal.updateTotal === 'function') {
        headerTotal.updateTotal(cart);
      }

      // Update free shipping bar if custom element exists
      const freeShipBar = document.querySelector('free-ship-progress-bar');
      if (freeShipBar && typeof freeShipBar.init === 'function') {
        freeShipBar.init(cart.items_subtotal_price);
      }
    } catch (_) {}
  }

  /* ---- Helper: Format Money ---- */
  function formatMoney(cents) {
    if (cents == null) return '';
    const format = window.cartStrings?.money_format ||
                   window.themeGlobalVariables?.settings?.money_format ||
                   'Rs. {{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (_) {}
    }
    const amount = (cents / 100).toFixed(0);
    return `Rs. ${Number(amount).toLocaleString('en-IN')}`;
  }

  /* ---- Helper: Image sizing ---- */
  function getSizedImageUrl(src, width) {
    if (!src) return '';
    if (src.includes('cdn.shopify.com')) {
      const separator = src.includes('?') ? '&' : '?';
      return `${src}${separator}width=${width}`;
    }
    return src;
  }

  /* ---- Helper: Escape HTML ---- */
  function escapeHtml(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

})();
